import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {SplitPageLayout} from '@primer/react'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {PullRequestFileTree} from '@github-ui/pull-request-file-tree/PullRequestFileTree'
import {PullRequestFilesToolbar} from '@github-ui/pull-request-files-toolbar/PullRequestFilesToolbar'
import {PullRequestDiffsList} from '../components/PullRequestDiffsList'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import type {DiffEntry} from '@github-ui/diff-lines'

export function FilesEntrypoint() {
  return (
    <ErrorBoundary critical fallback={<PullRequestErrorState text="Changes cannot be loaded" />}>
      <Files />
    </ErrorBoundary>
  )
}

export function Files() {
  const data = useRoutePayload<FilesRoutePayload>()

  return <FilesComponent {...data} />
}

export function FilesComponent(payload: FilesRoutePayload) {
  const {
    annotations,
    commits,
    diffSummaries,
    diffContents,
    pullRequest,
    repository,
    threadPreviews,
    user,
    viewerPendingReview,
  } = payload

  const diffsEntries: DiffEntry[] = diffContents.map(diff => ({
    ...diff,
    helpUrl: '',
    repository,
    diffContext: 'pr',
    currentUser: {
      avatarURL: '', // TODO
      login: user.currentUserLogin || '',
      tabSize: undefined,
      splitPreference: 'unified',
      lineSpacing: 'compact',
      canComment: true,
      commentsPreference: 'collapsed',
    },
    commentingEnabled: false,
    changeType: diff.status,
    // pullRequest, // TODO might need to do some mapping here
  }))

  return (
    <>
      <SplitPageLayout.Pane
        position="start"
        divider="none"
        sticky
        offsetHeader="80px" // sticky header height + a lil extra for spacing
        aria-label="File tree"
        padding="none"
        className="pr-3"
      >
        <PullRequestFileTree
          baseRefOid={pullRequest.comparison.baseOid}
          commits={commits}
          diffs={diffSummaries}
          ownerLogin={repository.ownerLogin}
          pathName={pullRequest.pathName}
          pullRequestId={pullRequest.globalRelayId}
          pullRequestNumber={pullRequest.number}
          repositoryName={repository.name}
        />
      </SplitPageLayout.Pane>
      <SplitPageLayout.Content as="div" width="full" padding="none">
        <PullRequestFilesToolbar
          annotations={annotations}
          copilotAccessAllowed={repository.copilotEnabled}
          currentUserLogin={user.currentUserLogin}
          isFileTreeExpanded={user.isFileTreeExpanded}
          pullRequest={pullRequest}
          repository={repository}
          shouldShowViewedFilesCount={user.shouldShowViewedFilesCount}
          totalFilesCount={diffSummaries.length}
          threadPreviews={threadPreviews}
          viewedFilesCount={user.viewedFilesCount}
          viewerPendingReview={viewerPendingReview}
          viewSettings={user.viewSettings}
        />
        <PullRequestDiffsList diffs={diffsEntries} />
      </SplitPageLayout.Content>
    </>
  )
}

try{ FilesEntrypoint.displayName ||= 'FilesEntrypoint' } catch {}
try{ Files.displayName ||= 'Files' } catch {}
try{ FilesComponent.displayName ||= 'FilesComponent' } catch {}